import { useDarkMode } from "../../../context/ThemeContext";
import { getThemeClasses } from "../../../utils/ThemeClasses";

interface PaymentHistory {
  id: string;
  fecha: string;
  descripcion: string;
  numeroFactura: string;
  periodo: string;
  monto: string;
  estado: "pagado" | "vencido" | "fallido";
}

export default function HistorialSection() {
  const { isDarkMode } = useDarkMode();
  const themeClasses = getThemeClasses(isDarkMode);

  const payments: PaymentHistory[] = [
    {
      id: "1",
      fecha: "15 May 2024",
      descripcion: "Plan Enterprise - Mayo",
      numeroFactura: "INV-2024-0515",
      periodo: "1-31 Mayo 2024",
      monto: "$3,155.20",
      estado: "vencido",
    },
    {
      id: "2",
      fecha: "15 Abr 2024",
      descripcion: "Plan Enterprise - Abril",
      numeroFactura: "INV-2024-0415",
      periodo: "1-30 Abril 2024",
      monto: "$3,155.20",
      estado: "pagado",
    },
    {
      id: "3",
      fecha: "15 Mar 2024",
      descripcion: "Plan Enterprise - Marzo",
      numeroFactura: "INV-2024-0315",
      periodo: "1-31 Marzo 2024",
      monto: "$3,155.20",
      estado: "pagado",
    },
    {
      id: "4",
      fecha: "20 Feb 2024",
      descripcion: "Plan Enterprise - Febrero",
      numeroFactura: "INV-2024-0220",
      periodo: "1-29 Feb 2024",
      monto: "$3,155.20",
      estado: "fallido",
    },
    {
      id: "5",
      fecha: "15 Ene 2024",
      descripcion: "Plan Enterprise - Enero",
      numeroFactura: "INV-2024-0115",
      periodo: "1-31 Enero 2024",
      monto: "$3,155.20",
      estado: "pagado",
    },
    {
      id: "6",
      fecha: "15 Dic 2023",
      descripcion: "Plan Enterprise - Diciembre",
      numeroFactura: "INV-2023-1215",
      periodo: "1-31 Dic 2023",
      monto: "$3,155.20",
      estado: "pagado",
    },
  ];

  const getStatusBadge = (estado: string) => {
    switch (estado) {
      case "pagado":
        return (
          <span className="text-primary-500 font-medium">Pagado</span>
        );
      case "vencido":
        return (
          <span className="text-red-500 font-medium">Vencido</span>
        );
      case "fallido":
        return (
          <span className="text-orange-500 font-medium">Fallido</span>
        );
      default:
        return null;
    }
  };

  const inputClasses = `px-3 py-2 ${isDarkMode ? 'bg-dark-surface' : 'bg-white'} border ${themeClasses.cardBorder} rounded-lg ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-primary-500/50`;

  return (
    <div className="w-full h-full p-4 sm:p-6 overflow-auto">
      {/* Filters Card */}
      <div className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl p-3 sm:p-4 mb-4 sm:mb-6`}>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 items-end">
          {/* Date Range */}
          <div className="sm:col-span-2 lg:col-span-1">
            <label className={`block text-xs ${themeClasses.textSecondary} mb-1`}>Rango de fechas</label>
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <input
                  type="text"
                  placeholder="Desde"
                  className={`${inputClasses} w-full pr-8 text-sm`}
                />
                <i className={`ri-calendar-line absolute right-3 top-1/2 -translate-y-1/2 ${themeClasses.textSecondary}`}></i>
              </div>
              <div className="relative flex-1">
                <input
                  type="text"
                  placeholder="Hasta"
                  className={`${inputClasses} w-full pr-8 text-sm`}
                />
                <i className={`ri-calendar-line absolute right-3 top-1/2 -translate-y-1/2 ${themeClasses.textSecondary}`}></i>
              </div>
            </div>
          </div>

          {/* Estado Filter */}
          <div>
            <label className={`block text-xs ${themeClasses.textSecondary} mb-1`}>Estado</label>
            <div className="relative">
              <select className={`${inputClasses} w-full appearance-none cursor-pointer pr-8`}>
                <option value="">Todos los estados</option>
                <option value="pagado">Pagado</option>
                <option value="vencido">Vencido</option>
                <option value="fallido">Fallido</option>
              </select>
              <i className={`ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 ${themeClasses.textSecondary}`}></i>
            </div>
          </div>

          {/* Search */}
          <div>
            <label className={`block text-xs ${themeClasses.textSecondary} mb-1`}>Buscar factura</label>
            <div className="relative">
              <i className={`ri-search-line absolute left-3 top-1/2 -translate-y-1/2 ${themeClasses.textSecondary}`}></i>
              <input
                type="text"
                placeholder="Número de factura o descripción"
                className={`${inputClasses} w-full pl-9`}
              />
            </div>
          </div>

          {/* Clear Filters */}
          <div>
            <button className="text-primary-500 hover:text-[var(--brand-primary-hover)] font-medium text-sm whitespace-nowrap">
              Limpiar filtros
            </button>
          </div>
        </div>
      </div>

      {/* Payments Table */}
      <div className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl overflow-hidden`}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className={`border-b ${themeClasses.cardBorder}`}>
                <th className={`text-left p-4 text-sm font-medium ${themeClasses.textSecondary}`}>
                  <div className="flex items-center space-x-1 cursor-pointer hover:text-primary-500">
                    <span>Fecha</span>
                    <i className="ri-arrow-up-down-line text-xs"></i>
                  </div>
                </th>
                <th className={`text-left p-4 text-sm font-medium ${themeClasses.textSecondary}`}>
                  <div className="flex items-center space-x-1 cursor-pointer hover:text-primary-500">
                    <span>Descripción</span>
                    <i className="ri-arrow-up-down-line text-xs"></i>
                  </div>
                </th>
                <th className={`text-left p-4 text-sm font-medium ${themeClasses.textSecondary}`}>
                  <div className="flex items-center space-x-1 cursor-pointer hover:text-primary-500">
                    <span>Número de factura</span>
                    <i className="ri-arrow-up-down-line text-xs"></i>
                  </div>
                </th>
                <th className={`text-left p-4 text-sm font-medium ${themeClasses.textSecondary}`}>
                  <div className="flex items-center space-x-1 cursor-pointer hover:text-primary-500">
                    <span>Periodo</span>
                    <i className="ri-arrow-up-down-line text-xs"></i>
                  </div>
                </th>
                <th className={`text-right p-4 text-sm font-medium ${themeClasses.textSecondary}`}>
                  <div className="flex items-center justify-end space-x-1 cursor-pointer hover:text-primary-500">
                    <span>Monto</span>
                    <i className="ri-arrow-up-down-line text-xs"></i>
                  </div>
                </th>
                <th className={`text-left p-4 text-sm font-medium ${themeClasses.textSecondary}`}>Estado</th>
                <th className={`text-center p-4 text-sm font-medium ${themeClasses.textSecondary}`}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {payments.map((payment) => (
                <tr 
                  key={payment.id} 
                  className={`border-b ${themeClasses.cardBorder} ${isDarkMode ? 'hover:bg-dark-surface' : 'hover:bg-gray-50'} transition-colors`}
                >
                  <td className={`p-4 text-sm ${themeClasses.text}`}>
                    {payment.fecha}
                  </td>
                  <td className={`p-4 text-sm ${themeClasses.text} font-medium`}>
                    {payment.descripcion}
                  </td>
                  <td className={`p-4 text-sm ${themeClasses.textSecondary}`}>
                    {payment.numeroFactura}
                  </td>
                  <td className={`p-4 text-sm ${themeClasses.textSecondary}`}>
                    {payment.periodo}
                  </td>
                  <td className={`p-4 text-sm ${themeClasses.text} font-semibold text-right`}>
                    {payment.monto}
                  </td>
                  <td className="p-4 text-sm">
                    {getStatusBadge(payment.estado)}
                  </td>
                  <td className="p-4">
                    <div className="flex items-center justify-center space-x-2">
                      <button className="flex items-center space-x-1 text-red-500 hover:text-red-600 text-sm font-medium">
                        <i className="ri-file-pdf-line"></i>
                        <span>PDF</span>
                      </button>
                      <button aria-label="Más opciones" className={`p-1 ${themeClasses.textSecondary} hover:${themeClasses.text}`}>
                        <i className="ri-more-2-fill"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
